<template>
  <div class="invite-creator">
    <h1 class="search-title highlight">Créer une invitation</h1>
    <p class="access-link">
      Compte déjà créé ?
      <router-link :to="{ name: 'accessManager' }">Workspace, prolongation, test ou abonné</router-link>
      ·
      <router-link :to="{ name: 'quoteBuilder' }">Générer un devis</router-link>
      ·
      <router-link :to="{ name: 'clientCities' }">Clients par ville</router-link>
    </p>

    <!-- Chatbox IA -->
    <div class="ai-chatbox">
      <div class="ai-header">
        <div class="ai-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 16 16">
            <path d="M5 2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm4.5 9a.5.5 0 0 1-1 0V8.5h-2a.5.5 0 0 1 0-1h2V5a.5.5 0 0 1 1 0v2.5h2a.5.5 0 0 1 0 1h-2z"/>
          </svg>
        </div>
        <div>
          <h2 class="ai-title">Assistant IA - Remplissage automatique</h2>
          <p class="ai-subtitle">Décrivez le prospect en langage naturel et l'IA remplira automatiquement le formulaire</p>
        </div>
      </div>

      <div class="ai-input-container">
        <textarea
          v-model="aiMessage"
          @keydown.ctrl.enter="parseWithAI"
          placeholder="Ex: Je veux créer un essai pour Marie Dubois, elle travaille chez Lexis Avocats, son email est marie.dubois@lexis.fr, téléphone 06 12 34 56 78. C'est un lead LinkedIn et on veut lui donner 14 jours d'essai."
          rows="4"
          class="form-control ai-textarea"
          :disabled="isParsingAI"
        ></textarea>
        <p class="ai-hint">💡 Astuce : Appuyez sur Ctrl+Entrée pour envoyer</p>
      </div>

      <div class="ai-actions">
        <button
          @click="parseWithAI"
          :disabled="isParsingAI || !aiMessage.trim()"
          class="btn button-base tech-purple ai-submit-button"
        >
          <div v-if="isParsingAI" class="mini-spinner"></div>
          <span v-if="isParsingAI">Analyse en cours...</span>
          <span v-else>✨ Remplir automatiquement</span>
        </button>
        <button
          @click="clearAIChat"
          :disabled="isParsingAI"
          class="btn button-base outline-button"
        >
          Effacer
        </button>
      </div>

      <div v-if="aiError" class="ai-message ai-message-error">
        {{ aiError }}
      </div>

      <div v-if="aiSuccess" class="ai-message ai-message-success">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
        </svg>
        {{ aiSuccess }}
      </div>
    </div>

    <div class="form-container">
      <!-- Informations de contact -->
      <div class="form-section">
        <h3 class="form-section-title">Informations du prospect (CRM)</h3>

        <div class="form-grid">
          <div class="form-field">
            <label for="civilite" class="form-label">Civilité :</label>
            <select
              id="civilite"
              v-model="civilite"
              class="form-input form-select"
            >
              <option value="">-- Sélectionner --</option>
              <option value="Monsieur">Monsieur</option>
              <option value="Madame">Madame</option>
            </select>
          </div>
          <div class="form-field">
            <label for="first-name" class="form-label">Prénom :</label>
            <input
              type="text"
              id="first-name"
              v-model="firstName"
              placeholder="Prénom"
              class="form-input"
            />
          </div>
        </div>

        <div class="form-grid">
          <div class="form-field">
            <label for="last-name" class="form-label">Nom :</label>
            <input
              type="text"
              id="last-name"
              v-model="lastName"
              placeholder="Nom"
              class="form-input"
            />
          </div>
          <div class="form-field">
            <label for="phone" class="form-label">Téléphone :</label>
            <input
              type="tel"
              id="phone"
              v-model="phone"
              placeholder="+33 6 12 34 56 78"
              class="form-input"
            />
          </div>
        </div>

        <div class="form-field">
          <label for="company" class="form-label">Société / Cabinet :</label>
          <input
            type="text"
            id="company"
            v-model="company"
            placeholder="Nom de la société ou du cabinet"
            class="form-input"
          />
        </div>
      </div>

      <div class="form-field">
        <label for="email" class="form-label">Email :</label>
        <input
          type="email"
          id="email"
          v-model="email"
          placeholder="exemple@domaine.com"
          class="form-input"
          required
        />
      </div>

      <div class="form-field">
        <label for="trial-days" class="form-label">Période d'essai (jours) :</label>
        <input
          type="number"
          id="trial-days"
          v-model="trialDays"
          min="1"
          max="365"
          class="form-input"
          required
        />
      </div>

      <!-- Informations CRM supplémentaires -->
      <div class="form-section">
        <h3 class="form-section-title">Informations CRM supplémentaires</h3>

        <div class="form-field">
          <label class="form-label">Source :</label>
          <div class="source-options">
            <label class="source-radio-label">
              <input
                type="radio"
                v-model="source"
                value="Inbound"
                class="crm-radio"
              />
              <span>Inbound <span class="crm-type-hint">(le prospect est venu à nous)</span></span>
            </label>
            <label class="source-radio-label">
              <input
                type="radio"
                v-model="source"
                value="Outbound"
                class="crm-radio"
              />
              <span>Outbound <span class="crm-type-hint">(on l'a contacté)</span></span>
            </label>
          </div>
        </div>

        <div v-if="source === 'Inbound'" class="form-field">
          <label for="canal-inbound" class="form-label">Canal inbound :</label>
          <select
            id="canal-inbound"
            v-model="canalInbound"
            class="form-input form-select"
          >
            <option value="">-- Sélectionner --</option>
            <option value="Web Download">Site Web</option>
            <option value="google_ads">google_ads</option>
            <option value="Google AdWords">Google AdWords</option>
            <option value="Réseau">Réseau</option>
            <option value="External Referral">Référence externe</option>
            <option value="Partner">Partenaire</option>
            <option value="Trade Show">Salon</option>
            <option value="Advertisement">Publicité</option>
            <option value="Web Research">Moteur de recherche</option>
            <option value="Facebook">Facebook</option>
          </select>
        </div>

        <div v-if="source === 'Outbound'" class="form-field">
          <label for="canal-outbound" class="form-label">Canal outbound :</label>
          <select
            id="canal-outbound"
            v-model="canalOutbound"
            class="form-input form-select"
          >
            <option value="">-- Sélectionner --</option>
            <option value="Cold call">Cold call</option>
            <option value="Linkedin">Linkedin</option>
          </select>
        </div>

        <div class="form-field">
          <label for="description" class="form-label">Notes / Description :</label>
          <textarea
            id="description"
            v-model="description"
            placeholder="Contexte, notes sur le prospect, besoins identifiés..."
            rows="3"
            class="form-input form-textarea"
          ></textarea>
        </div>
      </div>

      <!-- Cadence - E-mails de relance -->
      <div class="form-section cadence-section">
        <div class="form-field cadence-toggle-field">
          <label class="form-checkbox-label cadence-toggle-label">
            <input
              type="checkbox"
              :checked="cadenceEnabled"
              @change="onCadenceToggle"
              class="form-checkbox"
            />
            <span class="form-checkbox-text">Cadence</span>
          </label>
          <p class="form-checkbox-hint">Afficher les e-mails de relance (première relance, deuxième relance, laisser terminer)</p>
        </div>

        <div v-if="cadenceError" class="cadence-error message-banner message-error">
          {{ cadenceError }}
        </div>
        <div v-if="cadenceEnabled" class="cadence-emails">

          <!-- J+3 -->
          <div class="cadence-email-block">
            <div class="cadence-email-header">
              <label class="form-checkbox-label cadence-check-label">
                <input type="checkbox" v-model="cadence1Active" class="form-checkbox" />
                <span class="cadence-email-label">J+3</span>
              </label>
            </div>
            <div class="cadence-email-fields" :class="{ 'cadence-inactive': !cadence1Active }">
              <input
                :value="cadenceSubject1"
                @input="cadenceSubject1 = $event.target.value"
                type="text"
                class="form-input cadence-subject-input"
                placeholder="Objet de l'e-mail J+3..."
              />
              <textarea
                :value="displayedCadenceEmail1"
                @input="onCadenceEmail1Input"
                rows="9"
                class="form-input form-textarea cadence-textarea"
                placeholder="Corps de l'e-mail J+3..."
              ></textarea>
            </div>
          </div>

          <!-- J+7 -->
          <div class="cadence-email-block">
            <div class="cadence-email-header">
              <label class="form-checkbox-label cadence-check-label">
                <input type="checkbox" v-model="cadence2Active" class="form-checkbox" />
                <span class="cadence-email-label">J+7</span>
              </label>
            </div>
            <div class="cadence-email-fields" :class="{ 'cadence-inactive': !cadence2Active }">
              <input
                :value="cadenceSubject2"
                @input="cadenceSubject2 = $event.target.value"
                type="text"
                class="form-input cadence-subject-input"
                placeholder="Objet de l'e-mail J+7..."
              />
              <textarea
                :value="displayedCadenceEmail2"
                @input="onCadenceEmail2Input"
                rows="9"
                class="form-input form-textarea cadence-textarea"
                placeholder="Corps de l'e-mail J+7..."
              ></textarea>
            </div>
          </div>

          <!-- J+14 -->
          <div class="cadence-email-block">
            <div class="cadence-email-header">
              <label class="form-checkbox-label cadence-check-label">
                <input type="checkbox" v-model="cadence3Active" class="form-checkbox" />
                <span class="cadence-email-label">J+14</span>
              </label>
            </div>
            <div class="cadence-email-fields" :class="{ 'cadence-inactive': !cadence3Active }">
              <input
                :value="cadenceSubject3"
                @input="cadenceSubject3 = $event.target.value"
                type="text"
                class="form-input cadence-subject-input"
                placeholder="Objet de l'e-mail J+14..."
              />
              <textarea
                :value="displayedCadenceEmail3"
                @input="onCadenceEmail3Input"
                rows="9"
                class="form-input form-textarea cadence-textarea"
                placeholder="Corps de l'e-mail J+14..."
              ></textarea>
            </div>
          </div>

          <!-- J+30 -->
          <div class="cadence-email-block">
            <div class="cadence-email-header">
              <label class="form-checkbox-label cadence-check-label">
                <input type="checkbox" v-model="cadence4Active" class="form-checkbox" />
                <span class="cadence-email-label">J+30</span>
              </label>
            </div>
            <div class="cadence-email-fields" :class="{ 'cadence-inactive': !cadence4Active }">
              <input
                :value="cadenceSubject4"
                @input="cadenceSubject4 = $event.target.value"
                type="text"
                class="form-input cadence-subject-input"
                placeholder="Objet de l'e-mail J+30..."
              />
              <textarea
                :value="displayedCadenceEmail4"
                @input="onCadenceEmail4Input"
                rows="9"
                class="form-input form-textarea cadence-textarea"
                placeholder="Corps de l'e-mail J+30..."
              ></textarea>
            </div>
          </div>

          <!-- Après 1ère recherche : coché par défaut, jamais envoyé si la cadence est désactivée -->
          <div class="cadence-email-block">
            <div class="cadence-email-header">
              <label class="form-checkbox-label cadence-check-label">
                <input type="checkbox" v-model="cadenceFsActive" class="form-checkbox" />
                <span class="cadence-email-label">Après 1ère recherche</span>
              </label>
            </div>
            <p class="form-checkbox-hint">
              Envoyé ~35 min après la première recherche du prospect, si la case est cochée. Tokens : <code>[prénom]</code> (prénom du destinataire), <code>[signataire]</code> (votre nom).
            </p>
            <div class="cadence-email-fields" :class="{ 'cadence-inactive': !cadenceFsActive }">
              <input
                :value="cadenceSubjectFs"
                @input="cadenceSubjectFs = $event.target.value"
                type="text"
                class="form-input cadence-subject-input"
                placeholder="Objet de l'e-mail après 1ère recherche..."
              />
              <textarea
                :value="cadenceEmailFs"
                @input="cadenceEmailFs = $event.target.value"
                rows="9"
                class="form-input form-textarea cadence-textarea"
                placeholder="Corps de l'e-mail après 1ère recherche..."
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <div class="form-checkbox-container">
        <label class="form-checkbox-label">
          <input
            type="checkbox"
            v-model="addToCrm"
            class="form-checkbox"
          />
          <span class="form-checkbox-text">Ajouter au CRM (Zoho + HubSpot)</span>
        </label>
        <p class="form-checkbox-hint">Crée ou met à jour l'enregistrement dans Zoho CRM, et le contact dans HubSpot (sur un contact HubSpot existant, seuls les champs vides sont complétés)</p>

        <div v-if="addToCrm" class="crm-record-type-selector">
          <label class="crm-record-type-label">Type d'enregistrement CRM :</label>
          <div class="crm-record-type-options">
            <label class="crm-radio-label">
              <input
                type="radio"
                v-model="crmRecordType"
                value="lead"
                class="crm-radio"
              />
              <span>Prospect <span class="crm-type-hint">(Lead)</span></span>
            </label>
            <label class="crm-radio-label">
              <input
                type="radio"
                v-model="crmRecordType"
                value="contact"
                class="crm-radio"
              />
              <span>Contact</span>
            </label>
          </div>
        </div>
      </div>

      <button
        @click="createInvite"
        :disabled="isLoading"
        class="btn button-base tech-purple submit-button"
      >
        <div v-if="isLoading" class="mini-spinner"></div>
        {{ isLoading ? 'Création en cours...' : 'Créer l\'invitation' }}
      </button>
    </div>

    <div v-if="errorMessage" class="message-banner message-error">
      {{ errorMessage }}
    </div>

    <div v-if="inviteLink" class="result-container">
      <h2 class="result-title">Invitation créée</h2>

      <div class="result-field">
        <label class="form-label">Mot de passe temporaire :</label>
        <div class="result-copy-row">
          <input type="text" readonly v-model="tempPassword" class="form-input result-input" />
          <button @click="copyToClipboard(tempPassword, true)" class="btn button-base tech-purple copy-button">
            <span v-if="!passwordCopied">Copier</span>
            <span v-else>Copié ✓</span>
          </button>
        </div>
      </div>

      <div class="result-field">
        <label class="form-label">Fin de la période d'essai :</label>
        <div class="result-date">
          {{ formatDate(trialEndDate) }}
        </div>
      </div>

      <div class="result-field result-field-email">
        <label class="form-label">E-mail à envoyer :</label>
        <div class="prepared-email">
          <div class="prepared-email-line"><strong>Objet :</strong> {{ preparedEmailSubject }}</div>
          <pre class="prepared-email-body">{{ preparedEmailBody }}</pre>
          <button @click="copyPreparedEmail" class="btn button-base tech-purple copy-button">
            <span v-if="!emailCopied">Copier l'e-mail</span>
            <span v-else>Copié ✓</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const CADENCE_EMAIL_DEFAULTS = {
  subject1: 'Question sur vos retours sur Query Juriste',
  email1: `Bonjour Monsieur \${Leads.Last Name},

J'espère que vous allez bien.

Je voulais juste prendre de vos nouvelles depuis l'envoi de vos accès, avez-vous eu l'occasion de tester Query Juriste sur quelques recherches ?

N'hésitez pas à me faire signe si vous avez des questions.

Bonne journée,`,

  subject2: "Proposition d'échange téléphonique",
  email2: `Bonjour Monsieur \${Leads.Last Name},

J'espère que tout va bien de votre côté.

Je serais curieux de savoir si Query Juriste vous a déjà été utile sur des recherches concrètes. Vos retours m'intéressent vraiment, que ce soit positif ou s'il y a des choses à améliorer.

Si vous souhaitez qu'on en discute, dites-moi quand ça vous arrange.

Bonne journée,`,

  subject3: "Question sur votre utilisation de l'outil",
  email3: `Bonjour Monsieur \${Leads.Last Name},

J'espère que vous allez bien.

Je voulais savoir comment se passe votre expérience avec Query Juriste. Avez-vous eu l'occasion de l'utiliser sur des dossiers en cours ?

Je reste disponible si vous voulez échanger sur l'outil ou approfondir certaines fonctionnalités.

Bonne journée,`,

  subject4: "Proposition de prolongation de la période d'essai",
  email4: `Bonjour Monsieur \${Leads.Last Name},

J'espère que vous allez bien.

Votre période d'essai arrive bientôt à son terme. J'espère que Query Juriste vous a permis de gagner du temps sur vos recherches.

Si vous souhaitez maintenir votre accès, nous pouvons l'activer très rapidement. Et si vous avez des questions avant de décider, je suis disponible pour en parler.

Bonne journée,`,

  // Email envoyé ~35 min après la 1ère recherche de l'utilisateur (envoi SMTP côté
  // backend, PAS une cadence Zoho). Tokens résolus par le backend : [prénom] = prénom
  // du destinataire, [signataire] = nom de l'owner Zoho qui envoie.
  subjectFs: 'Proposition de feedback, débrief',
  emailFs: `Bonjour [prénom],

J'ai vu que vous aviez lancé votre première recherche sur Query. Je veux m'assurer que vous en tirez le maximum de vos prochaines recherches.

Qu'en avez-vous pensé ? Pertinence, sources, vitesse.

Si vous êtes partant, on peut faire un débrief de 15 minutes sur vos premières recherches. Dites-moi simplement si vous préférez en parler au téléphone, je reste disponible.

[signataire]`,
};

export default {
  name: 'InviteCreator',
  data() {
    return {
      email: '',
      trialDays: 30,
      isLoading: false,
      errorMessage: '',
      inviteLink: '',
      tempPassword: '',
      trialEndDate: null,
      linkCopied: false,
      passwordCopied: false,
      emailCopied: false,
      // Nouveaux champs CRM
      firstName: '',
      lastName: '',
      phone: '',
      company: '',
      civilite: '',
      source: '',         // "Inbound" / "Outbound"
      canalInbound: '',   // api_value Zoho Lead_Source (ex: "Web Download")
      canalOutbound: '',  // "Cold call" / "Linkedin"
      description: '',
      addToCrm: true,  // Coché par défaut
      crmRecordType: 'lead',  // 'lead' ou 'contact'
      // Cadence - e-mails de relance
      cadenceEnabled: false,
      cadenceEmail1: CADENCE_EMAIL_DEFAULTS.email1,
      cadenceEmail2: CADENCE_EMAIL_DEFAULTS.email2,
      cadenceEmail3: CADENCE_EMAIL_DEFAULTS.email3,
      cadenceEmail4: CADENCE_EMAIL_DEFAULTS.email4,
      cadenceEmailFs: CADENCE_EMAIL_DEFAULTS.emailFs,
      cadenceSubject1: CADENCE_EMAIL_DEFAULTS.subject1,
      cadenceSubject2: CADENCE_EMAIL_DEFAULTS.subject2,
      cadenceSubject3: CADENCE_EMAIL_DEFAULTS.subject3,
      cadenceSubject4: CADENCE_EMAIL_DEFAULTS.subject4,
      cadenceSubjectFs: CADENCE_EMAIL_DEFAULTS.subjectFs,
      cadence1Active: false,
      cadence2Active: false,
      cadence3Active: false,
      cadence4Active: false,
      cadenceFsActive: true,
      cadenceError: '',
      // Champs IA
      aiMessage: '',
      isParsingAI: false,
      aiError: '',
      aiSuccess: '',
      // Speech-to-text state
      isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      isTranscribing: false
    }
  },
  watch: {
    source(newVal) {
      if (newVal === 'Inbound') this.canalOutbound = '';
      else if (newVal === 'Outbound') this.canalInbound = '';
      else { this.canalInbound = ''; this.canalOutbound = ''; }
    },
  },
  computed: {
    preparedEmailSubject() {
      return 'Accès Query Juriste';
    },
    displayedCadenceEmail1() { return this.replaceCadencePlaceholders(this.cadenceEmail1); },
    displayedCadenceEmail2() { return this.replaceCadencePlaceholders(this.cadenceEmail2); },
    displayedCadenceEmail3() { return this.replaceCadencePlaceholders(this.cadenceEmail3); },
    displayedCadenceEmail4() { return this.replaceCadencePlaceholders(this.cadenceEmail4); },
    preparedEmailBody() {
      const prenom = this.firstName || '[prénom]';
      const email = this.email || '[email]';
      const motDePasse = this.tempPassword || '[mot de passe généré]';
      return `Bonjour ${prenom},

Comme promis, voici vos identifiants d'accès à l'outil :

Lien d'accès : https://www.query-juriste.com/loginPage
Identifiant : ${email}
Mot de passe : ${motDePasse}

N'hésitez pas à me faire part de vos retours !

Bien cordialement,`;
    }
  },
  methods: {
    async parseWithAI() {
      // Reset messages
      this.aiError = '';
      this.aiSuccess = '';

      if (!this.aiMessage.trim()) {
        this.aiError = 'Veuillez décrire le prospect';
        return;
      }

      this.isParsingAI = true;

      try {
        // Récupérer le token de session Supabase
        const { supabase } = await import('../supabase/supabaseClient');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          this.aiError = 'Vous devez être connecté pour utiliser l\'assistant IA';
          this.isParsingAI = false;
          return;
        }

        const token = session.access_token;

        const response = await fetch('https://api.query-juriste.com/copilot/parse-prospect-info', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ message: this.aiMessage })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Une erreur est survenue');
        }

        // Remplir les champs du formulaire avec les données parsées
        const data = result.data;

        if (data.civilite) this.civilite = data.civilite;
        if (data.firstName) this.firstName = data.firstName;
        if (data.lastName) this.lastName = data.lastName;
        if (data.email) this.email = data.email;
        if (data.phone) this.phone = data.phone;
        if (data.company) this.company = data.company;
        if (data.trialDays) this.trialDays = data.trialDays;
        if (data.source) this.source = data.source;
        if (data.canalInbound) this.canalInbound = data.canalInbound;
        if (data.canalOutbound) this.canalOutbound = data.canalOutbound;
        if (data.description) this.description = data.description;

        // Compter les champs remplis
        const filledFields = Object.keys(data).length;
        this.aiSuccess = `✨ ${filledFields} champ${filledFields > 1 ? 's' : ''} rempli${filledFields > 1 ? 's' : ''} automatiquement !`;

        // Scroll vers le formulaire
        setTimeout(() => {
          const formElement = document.querySelector('.form-container');
          if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);

      } catch (error) {
        this.aiError = error.message;
      } finally {
        this.isParsingAI = false;
      }
    },

    clearAIChat() {
      this.aiMessage = '';
      this.aiError = '';
      this.aiSuccess = '';
    },

    // ========== SPEECH-TO-TEXT METHODS ==========
    async toggleRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        await this.startRecording();
      }
    },

    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100
          }
        });

        const mimeType = this.getSupportedMimeType();
        this.mediaRecorder = new MediaRecorder(stream, { mimeType });
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = async () => {
          stream.getTracks().forEach(track => track.stop());
          await this.processRecording();
        };

        this.mediaRecorder.start();
        this.isRecording = true;

      } catch (error) {
        console.error('Error starting recording:', error);
        if (error.name === 'NotAllowedError') {
          this.aiError = 'Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.';
        } else if (error.name === 'NotFoundError') {
          this.aiError = 'Aucun microphone détecté.';
        } else {
          this.aiError = 'Erreur lors du démarrage de l\'enregistrement.';
        }
      }
    },

    getSupportedMimeType() {
      const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/wav'
      ];

      for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
          return type;
        }
      }
      return 'audio/webm';
    },

    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
        this.isRecording = false;
      }
    },

    async processRecording() {
      if (this.audioChunks.length === 0) {
        this.aiError = 'Aucun audio enregistré.';
        return;
      }

      this.isTranscribing = true;
      this.aiError = '';

      try {
        const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });

        if (audioBlob.size < 1000) {
          this.aiError = 'Enregistrement trop court. Veuillez réessayer.';
          return;
        }

        let extension = 'webm';
        if (mimeType.includes('mp4')) extension = 'mp4';
        else if (mimeType.includes('ogg')) extension = 'ogg';
        else if (mimeType.includes('wav')) extension = 'wav';

        const formData = new FormData();
        formData.append('audio', audioBlob, `recording.${extension}`);
        formData.append('language', 'fr');

        const { supabase } = await import('../supabase/supabaseClient');
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;

        if (!token) {
          this.aiError = 'Session expirée. Veuillez vous reconnecter.';
          return;
        }

        const response = await fetch('https://api.query-juriste.com/copilot/speech-to-text', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Erreur serveur: ${response.status}`);
        }

        const result = await response.json();
        const transcribedText = result.text?.trim();

        if (transcribedText) {
          this.aiMessage = transcribedText;
          this.aiSuccess = '🎤 Texte transcrit avec succès !';
          setTimeout(() => this.aiSuccess = '', 3000);
        } else {
          this.aiError = 'Aucun texte détecté. Veuillez réessayer.';
        }

      } catch (error) {
        console.error('Error transcribing audio:', error);
        this.aiError = `Erreur: ${error.message}`;
      } finally {
        this.isTranscribing = false;
        this.audioChunks = [];
      }
    },

    async createInvite() {
      // Reset error messages
      this.errorMessage = '';

      if (!this.email) {
        this.errorMessage = 'Veuillez entrer une adresse email';
        return;
      }

      if (!this.trialDays || this.trialDays < 1) {
        this.errorMessage = 'Veuillez entrer une période d\'essai valide';
        return;
      }

      this.isLoading = true;
      this.inviteLink = '';
      this.tempPassword = '';
      this.linkCopied = false;
      this.passwordCopied = false;
      this.emailCopied = false;

      try {
        // Récupérer le token de session Supabase
        const { supabase } = await import('../supabase/supabaseClient');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          this.errorMessage = 'Vous devez être connecté pour créer une invitation';
          this.isLoading = false;
          return;
        }

        const token = session.access_token;

        // Construire le payload avec les champs CRM
        const payload = {
          email: this.email,
          trial_days: this.trialDays,
          add_to_crm: this.addToCrm,
          crm_record_type: this.crmRecordType
        };

        // Ajouter les champs CRM s'ils sont remplis
        if (this.firstName) payload.first_name = this.firstName;
        if (this.lastName) payload.last_name = this.lastName;
        if (this.phone) payload.phone = this.phone;
        if (this.company) payload.company = this.company;
        if (this.civilite) payload.civilite = this.civilite;
        if (this.source) payload.source = this.source;
        if (this.canalInbound) payload.canal_inbound = this.canalInbound;
        if (this.canalOutbound) payload.canal_outbound = this.canalOutbound;
        if (this.description) payload.description = this.description;

        // Cadences : 2 modes exclusifs — soit cadence par défaut (InviteH/InviteF), soit cadence personnalisée (4 emails)
        const civ = this.civilite || '';
        const nom = this.lastName || (this.email ? this.email.split('@')[0] : '');
        const cadenceMap = [
          { active: this.cadence1Active, key: 'j3',  subject: this.cadenceSubject1, body: this.cadenceEmail1 },
          { active: this.cadence2Active, key: 'j7',  subject: this.cadenceSubject2, body: this.cadenceEmail2 },
          { active: this.cadence3Active, key: 'j14', subject: this.cadenceSubject3, body: this.cadenceEmail3 },
          { active: this.cadence4Active, key: 'j30', subject: this.cadenceSubject4, body: this.cadenceEmail4 },
        ];
        const hasCustomCadence = this.cadenceEnabled && cadenceMap.some(
          c => c.active && (c.subject || '').trim() && (c.body || '').trim()
        );
        const useDefaultCadence = this.cadenceEnabled && !hasCustomCadence && (this.civilite === 'Monsieur' || this.civilite === 'Madame');
        if (hasCustomCadence) {
          const cadences = [];
          const sfx = { j3: '', j7: '7', j14: '141', j30: '30' };
          for (const c of cadenceMap) {
            if (c.active && c.subject && c.body) {
              const replacePlaceholder = (text) =>
                (civ && nom)
                  ? text.replace(/(Monsieur|Madame) \$\{Leads\.Last Name\}/g, civ + ' ' + nom)
                  : text;
              cadences.push({
                key: c.key,
                suffix: sfx[c.key],
                subject: replacePlaceholder(c.subject),
                body: replacePlaceholder(c.body)
              });
            }
          }
          if (cadences.length) payload.cadences = cadences;
        } else if (useDefaultCadence) {
          payload.use_default_cadence = true;
        }

        // Email "1ère recherche" : seulement si la cadence est activée ET sa case cochée.
        // Le backend persiste le texte (défaut ou personnalisé) dans le champ Zoho et
        // rend le user éligible à l'email de feedback ; sans `first_search_email`, le
        // user n'est jamais éligible. Tokens [prénom]/[signataire] résolus côté backend
        // (pas par le merge Zoho).
        if (this.cadenceEnabled && this.cadenceFsActive) {
          payload.first_search_email = {
            subject: (this.cadenceSubjectFs || '').trim(),
            body: (this.cadenceEmailFs || '').trim(),
          };
        }

        const response = await fetch('https://api.query-juriste.com/copilot/create-invite', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Une erreur est survenue');
        }

        this.inviteLink = data.invite_link;
        this.tempPassword = data.temp_password;
        this.trialEndDate = data.trial_end_date;

        // Afficher un message si le lead a été ajouté au CRM
        if (data.crm_synced) {
          // Optional: show CRM sync confirmation
        }
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.isLoading = false;
      }
    },

    copyToClipboard(text, isPassword = false) {
      navigator.clipboard.writeText(text)
        .then(() => {
          if (isPassword) {
            this.passwordCopied = true;
            setTimeout(() => this.passwordCopied = false, 2000);
          } else {
            this.linkCopied = true;
            setTimeout(() => this.linkCopied = false, 2000);
          }
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    },

    copyPreparedEmail() {
      const fullEmail = `Objet: ${this.preparedEmailSubject}\n\n${this.preparedEmailBody}`;
      navigator.clipboard.writeText(fullEmail)
        .then(() => {
          this.emailCopied = true;
          setTimeout(() => this.emailCopied = false, 2000);
        })
        .catch(err => {
          console.error('Failed to copy email: ', err);
        });
    },

    formatDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    replaceCadencePlaceholders(text) {
      if (!text) return '';
      const civ = this.civilite || '';
      const nom = this.lastName || '';
      if (!civ || !nom) return text;
      return text.replace(/(Monsieur|Madame) \$\{Leads\.Last Name\}/g, civ + ' ' + nom);
    },

    restoreCadencePlaceholders(text) {
      if (!text) return '';
      const civ = this.civilite || '';
      const nom = this.lastName || '';
      if (!civ || !nom) return text;
      const escapedNom = nom.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return text.replace(new RegExp(civ + ' ' + escapedNom, 'g'), civ + ' ${Leads.Last Name}');
    },

    onCadenceToggle(e) {
      const wantEnable = e.target.checked;
      if (wantEnable) {
        const civ = (this.civilite || '').trim();
        const nom = (this.lastName || '').trim();
        if (!civ || !nom) {
          this.cadenceError = 'Veuillez remplir la civilité et le nom du prospect pour pouvoir activer la cadence.';
          e.target.checked = false;
          return;
        }
        this.cadenceError = '';
      } else {
        this.cadenceError = '';
      }
      this.cadenceEnabled = wantEnable;
    },

    onCadenceEmail1Input(e) {
      this.cadenceEmail1 = this.restoreCadencePlaceholders(e.target.value);
    },
    onCadenceEmail2Input(e) {
      this.cadenceEmail2 = this.restoreCadencePlaceholders(e.target.value);
    },
    onCadenceEmail3Input(e) {
      this.cadenceEmail3 = this.restoreCadencePlaceholders(e.target.value);
    },
    onCadenceEmail4Input(e) {
      this.cadenceEmail4 = this.restoreCadencePlaceholders(e.target.value);
    }
  },
}
</script>

<style scoped>
.access-link {
  text-align: center;
  margin: -10px 0 20px;
  font-size: 14px;
  color: #555;
}
.access-link a {
  color: #625CFB;
  font-weight: 600;
}

/* ========== BASE LAYOUT ========== */
.invite-creator {
  padding: 20px;
  font-family: 'Roboto', sans-serif;
  background-color: #F5F6FA;
  min-height: 100vh;
  max-width: 875px;
  margin: 0 auto;
}

/* ========== TITLE ========== */
.search-title {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 24px;
}

.highlight {
  background: linear-gradient(90deg, #000000 0%, #625CFB 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

/* ========== AI CHATBOX ========== */
.ai-chatbox {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ai-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}

.ai-icon {
  background: linear-gradient(135deg, #6c63ff 0%, #5a54d1 100%);
  border-radius: 12px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.3);
  flex-shrink: 0;
}

.ai-icon svg {
  color: white;
}

.ai-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
}

.ai-subtitle {
  font-size: 0.85rem;
  color: #718096;
  margin: 0;
  font-style: italic;
}

.ai-input-container {
  margin-bottom: 16px;
}

.ai-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e4e8;
  border-radius: 12px;
  font-size: 0.9rem;
  resize: vertical;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #f8f9fa;
  box-sizing: border-box;
}

.ai-textarea:focus {
  outline: none;
  border-color: #6c63ff;
  background: white;
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.1);
}

.ai-textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.ai-textarea::placeholder {
  color: rgba(0, 0, 0, 0.35);
  opacity: 1;
}

.ai-hint {
  font-size: 0.75rem;
  color: #a0aec0;
  margin-top: 6px;
  margin-bottom: 0;
}

.ai-actions {
  display: flex;
  gap: 12px;
}

.ai-submit-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.ai-message {
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ai-message-error {
  background: linear-gradient(135deg, rgba(255, 82, 82, 0.08), rgba(255, 82, 82, 0.12));
  color: #e53e3e;
  border: 1px solid rgba(255, 82, 82, 0.15);
}

.ai-message-success {
  background: linear-gradient(135deg, rgba(72, 187, 120, 0.08), rgba(72, 187, 120, 0.12));
  color: #2f855a;
  border: 1px solid rgba(72, 187, 120, 0.15);
}

/* ========== FORM CONTAINER ========== */
.form-container {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
}

.form-section {
  border-top: 1px solid #e0e4e8;
  padding-top: 20px;
  margin-top: 20px;
  margin-bottom: 20px;
}

.form-section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  background: linear-gradient(90deg, #333 0%, #6c63ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-field {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e0e4e8;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: inherit;
  transition: all 0.2s ease;
  background-color: #f8f9fa;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #6c63ff;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.1);
}

.form-input::placeholder {
  color: rgba(0, 0, 0, 0.35);
  opacity: 1;
}

.form-select {
  appearance: auto;
  cursor: pointer;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

/* Cadence - e-mails de relance */
.cadence-section {
  margin-top: 24px;
}

.cadence-toggle-field {
  margin-bottom: 16px;
}

.cadence-error {
  margin-top: 12px;
}

.cadence-toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cadence-emails {
  margin-top: 16px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 10px;
  border: 1px solid #e0e4e8;
}

.cadence-email-block {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e4e8;
}

.cadence-email-block:last-of-type {
  margin-bottom: 8px;
  border-bottom: none;
}

.cadence-email-header {
  margin-bottom: 10px;
}

.cadence-check-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.cadence-email-label {
  font-weight: 700;
  color: #333;
  font-size: 0.95rem;
}

.cadence-email-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cadence-inactive {
  opacity: 0.45;
  pointer-events: none;
}

.cadence-subject-input {
  font-size: 0.9rem;
}

.cadence-textarea {
  width: 100%;
  min-height: 180px;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.5;
}

.field-error {
  margin-top: 6px;
  color: #e53e3e;
  font-size: 0.8rem;
}

/* ========== CHECKBOX ========== */
.form-checkbox-container {
  margin-bottom: 20px;
}

.form-checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.form-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 10px;
  accent-color: #6c63ff;
  cursor: pointer;
}

.form-checkbox-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
}

.form-checkbox-hint {
  font-size: 0.8rem;
  color: #a0aec0;
  margin-top: 4px;
  margin-left: 28px;
  margin-bottom: 0;
}

.crm-record-type-selector {
  margin-top: 10px;
  margin-left: 28px;
}

.crm-record-type-label {
  font-size: 0.85rem;
  color: #555;
  font-weight: 500;
  margin-bottom: 6px;
  display: block;
}

.crm-record-type-options {
  display: flex;
  gap: 20px;
}

.crm-radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;
}

.crm-radio {
  width: 16px;
  height: 16px;
  accent-color: #6c63ff;
  cursor: pointer;
}

.crm-type-hint {
  font-size: 0.78rem;
  color: #a0aec0;
}

.source-options {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.source-radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;
}

/* ========== BUTTONS ========== */
.button-base {
  padding: 10px 25px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 500;
  border: none;
  transition: all 0.2s ease;
}

.tech-purple {
  background-color: #6c63ff;
  color: white;
}

.tech-purple:hover:not(:disabled) {
  background-color: #5a54d1;
}

.tech-purple:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.outline-button {
  background-color: white;
  color: #6c63ff;
  border: 1.5px solid #6c63ff;
}

.outline-button:hover:not(:disabled) {
  background-color: #f8f7ff;
}

.outline-button:disabled {
  background-color: #f5f5f5;
  color: #999;
  border-color: #e0e4e8;
  cursor: not-allowed;
}

.submit-button {
  width: 100%;
  padding: 12px 25px;
  font-size: 0.95rem;
  gap: 8px;
}

/* ========== SPINNER ========== */
.mini-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ========== ERROR BANNER ========== */
.message-banner {
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 24px;
  font-size: 0.9rem;
}

.message-error {
  background: linear-gradient(135deg, rgba(255, 82, 82, 0.08), rgba(255, 82, 82, 0.12));
  color: #e53e3e;
  border: 1px solid rgba(255, 82, 82, 0.15);
}

/* ========== RESULT CONTAINER ========== */
.result-container {
  background-color: #E9EFF4;
  background: linear-gradient(to right, #E9EFF4, #E2FAFFCC);
  border-radius: 12px;
  padding: 24px;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
}

.result-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  background: linear-gradient(90deg, #333 0%, #6c63ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.result-field {
  margin-bottom: 16px;
}

.result-copy-row {
  display: flex;
  gap: 0;
}

.result-input {
  flex: 1;
  border-radius: 10px 0 0 10px;
  background-color: white;
  border-right: none;
}

.result-input:focus {
  box-shadow: none;
  border-color: #e0e4e8;
  background-color: white;
}

.copy-button {
  border-radius: 0 10px 10px 0;
  padding: 10px 18px;
  white-space: nowrap;
  min-width: 90px;
}

.result-date {
  padding: 10px 14px;
  border: 2px solid #e0e4e8;
  border-radius: 10px;
  background-color: white;
  font-size: 0.9rem;
  color: #333;
}

.result-field-email {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e0e4e8;
}

.prepared-email {
  background-color: white;
  border: 2px solid #e0e4e8;
  border-radius: 10px;
  padding: 16px;
}

.prepared-email-line {
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: #333;
}

.prepared-email-body {
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #333;
  margin: 0 0 12px 0;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.prepared-email .copy-button {
  margin-top: 4px;
}

/* ========== MICROPHONE BUTTON STYLES ========== */
.mic-button {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #e0e4e8;
  background-color: white;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  flex-shrink: 0;
}

.mic-button:hover:not(:disabled) {
  border-color: #6c63ff;
  color: #6c63ff;
  background-color: #f8f7ff;
}

.mic-button.recording {
  border-color: #ff4444;
  background-color: #ff4444;
  color: white;
  animation: pulse-recording 1.5s ease-in-out infinite;
}

.mic-button.transcribing {
  border-color: #6c63ff;
  background-color: #f8f7ff;
  cursor: wait;
}

.mic-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@keyframes pulse-recording {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 68, 68, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 8px rgba(255, 68, 68, 0);
  }
}

.mic-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #e0e4e8;
  border-top-color: #6c63ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ========== RESPONSIVE ========== */
@media (max-width: 768px) {
  .invite-creator {
    padding: 16px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .ai-actions {
    flex-direction: column;
  }

  .ai-submit-button {
    width: 100%;
  }
}
</style>
